import React, { useEffect, useState } from 'react';
import CSVReader from 'react-csv-reader';
import PlayerDTO from '../../../../v2/DTO/PlayerDTO.js';
import { getAllV2Sport, postV2Player, postV2Team } from '../../../../api/index.js';
import useFetch from '../../../../v2/hooks/useFetch.js';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import TeamDTO from '../../../../v2/DTO/TeamDTO.js';

const normalizeIdentifier = value => value?.trim().toLowerCase();

const ActorsImport = ({ importType }) => {
  const [data, setData] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  const {
    data: sports,
    isLoading: sportsLoading,
    error: sportsError,
    fetchData: fetchSports,
  } = useFetch(getAllV2Sport, []);

  const {
    data: createdData,
    error,
    fetchData,
  } = useFetch(importType === 'players' ? postV2Player : postV2Team, [data]);

  useEffect(() => {
    setStep(1);
    setData([]);
    setLoading(false);
    setResult(null);
    setImportErrors([]);
  }, [importType]);

  useEffect(() => {
    fetchSports();
  }, []);

  const convertDateFormat = dateStr => {
    if (!dateStr) return undefined;

    const [day, month, year] = dateStr.trim().split('/');
    return day && month && year ? `${year}-${month}-${day}` : dateStr;
  };

  const handleFileLoad = csvData => {
    if (!Array.isArray(sports) || sports.length === 0) {
      setData([]);
      setImportErrors(['La liste des sports est indisponible. Recharge la page puis réessaie.']);
      setStep(2);
      return;
    }

    const validationErrors = [];

    const dtos = csvData.map((row, index) => {
      const lineNumber = index + 2;
      const provider = normalizeIdentifier(row.provider);
      const sportId = normalizeIdentifier(row.sport);
      const disciplineId = normalizeIdentifier(row.discipline);

      if (!provider || !sportId || !disciplineId) {
        validationErrors.push(
          `Ligne ${lineNumber} : provider, sport et discipline sont obligatoires.`
        );
        return null;
      }

      const foundSport = sports.find(sport =>
        normalizeIdentifier(sport.idName) === sportId &&
        normalizeIdentifier(sport.provider) === provider
      );

      if (!foundSport) {
        validationErrors.push(
          `Ligne ${lineNumber} : sport "${row.sport}" introuvable pour le provider "${row.provider}".`
        );
        return null;
      }

      const foundDiscipline = foundSport.disciplines?.find(discipline =>
        normalizeIdentifier(discipline.idName) === disciplineId &&
        normalizeIdentifier(discipline.provider) === provider
      );

      if (!foundDiscipline) {
        validationErrors.push(
          `Ligne ${lineNumber} : discipline "${row.discipline}" introuvable pour le sport ` +
          `"${row.sport}" et le provider "${row.provider}".`
        );
        return null;
      }

      const dtoData = {
        ...row,
        provider: row.provider.trim(),
        disciplines: [foundDiscipline],
      };

      if (importType === 'players') {
        return new PlayerDTO({
          ...dtoData,
          birthdate: convertDateFormat(row.birthdate),
        });
      }

      return new TeamDTO(dtoData);
    });

    setData(dtos.filter(Boolean));
    setImportErrors(validationErrors);
    setStep(2);
  };

  const handleSubmit = () => {
    if (data.length === 0 || importErrors.length > 0) return;

    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    setLoading(false);

    if (error) {
      setResult({ error: 'Failed to import data' });
      setStep(3);
    } else if (createdData) {
      setResult(createdData);
      setStep(3);
    }
  }, [createdData, error]);

  const renderTableHeader = () => {
    if (data.length === 0) return null;

    return (
      <TableHead>
        <TableRow>
          {Object.keys(data[0]).map(key => (
            <TableCell sx={{ fontWeight: 'bold' }} key={key}>{key}</TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  const renderTableRows = () => (
    <TableBody>
      {data.map((dto, index) => (
        <TableRow key={index}>
          {Object.values(dto).map((value, valueIndex) => {
            if (Array.isArray(value) && value.length > 0) {
              return (
                <TableCell key={valueIndex}>
                  {value[0].idName || JSON.stringify(value)}
                </TableCell>
              );
            }

            return (
              <TableCell key={valueIndex}>
                {typeof value === 'object' && value !== null ? JSON.stringify(value) : value}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </TableBody>
  );

  const renderImportInput = () => {
    if (sportsLoading) {
      return <CircularProgress size={24} />;
    }

    if (sportsError) {
      return <Alert severity="error">Impossible de charger la liste des sports.</Alert>;
    }

    return (
      <CSVReader
        onFileLoaded={handleFileLoad}
        parserOptions={{
          header: true,
          skipEmptyLines: true,
          encoding: 'UTF-8',
        }}
      />
    );
  };

  const renderValidationActions = () => (
    <Box m={5}>
      <Button onClick={() => setStep(1)}>Annuler</Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={data.length === 0 || importErrors.length > 0}
      >
        VALIDER
      </Button>
    </Box>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return renderImportInput();
      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography fontSize="1.2em">Etape de validation - {importType}</Typography>
            <Typography fontSize="1.2em">{data.length} {importType}</Typography>

            {importErrors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                {importErrors.map(message => <div key={message}>{message}</div>)}
              </Alert>
            )}

            {renderValidationActions()}

            <Table>
              {renderTableHeader()}
              {renderTableRows()}
            </Table>

            {renderValidationActions()}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6">Resultats - {importType}</Typography>
            {result ? (
              result.length ? (
                <Typography variant="body" fontSize="1.1em">
                  {result.length} {importType}
                </Typography>
              ) : (
                <pre>{JSON.stringify(result, null, 2)}</pre>
              )
            ) : (
              <p>No result</p>
            )}
            <br />
            <Button onClick={() => setStep(1)}>Importer un autre fichier</Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle">{importType}</Typography>
      {loading ? <p>Loading...</p> : renderStep()}
    </Box>
  );
};

export default ActorsImport;
